// Front-end for the Home page — the hero Lottie, the walkthrough accordions, the
// portrait faces fan, the discover scenarios carousel, and the scroll-reveal.
// Peer Vite entry — never imports the SCSS.

// lottie_light is the SVG-only build with expression support stripped (168KB min
// against the full player's 306KB). home-hero.json has neither expressions nor
// text layers, so nothing it drops is used — but check a new animation before
// pointing this entry at it.
import lottie from 'lottie-web/build/player/lottie_light';
import { createCarousel } from '../Assets/Carousel';
import { revealOnScroll } from '../Assets/scrollReveal';
import { prefersReducedMotion } from '../Assets/motion';

// ── Hero animation ───────────────────────────────────────────────────────────
// The hero art is a Lottie (480×480, 300 frames at 30fps = one continuous 10s
// loop) mounted into .stage from the JSON named by data-lottie. There is no
// poster still to swap out (see Index.cshtml for why), so the frame simply stays
// empty until the player paints — .gallery's aspect-ratio holds the box open, so
// nothing shifts, and a failed fetch just leaves it empty rather than breaking.
//
// The play/pause button's ring is redrawn from the animation's enterFrame event
// rather than a CSS animation of its own: one revolution IS one loop, off the
// player's clock, so the ring and the art share a single timebase and can never
// drift. Pausing just stops the redraws, so the ring freezes mid-circle and
// resumes from there, not from zero. Reduced motion starts the player paused —
// it only ever runs after an explicit press of play.
const stage = document.querySelector<HTMLElement>('.hero .gallery .stage');
const gallery = stage?.closest<HTMLElement>('.gallery');
const toggle = gallery?.querySelector<HTMLButtonElement>('.toggle');
const glyph = toggle?.querySelector<HTMLElement>('i');
const meter = gallery?.querySelector<SVGCircleElement>('.meter');
const source = stage?.dataset.lottie;

if (stage && gallery && toggle && glyph && meter && source) {
    const animation = lottie.loadAnimation({
        container: stage,
        renderer: 'svg',
        loop: true,
        // Armed below instead, once the reduced-motion preference has been read —
        // autoplay would run a frame or two before that check could stop it.
        autoplay: false,
        path: source,
    });

    // The r=21 ring's circumference (2π × 21) — the same constant seeds the
    // meter's stroke-dasharray in Index.cshtml.scss; keep the two in step.
    const CIRCUMFERENCE = 131.95;

    const setPaused = (paused: boolean): void => {
        if (paused) {
            animation.pause();
        } else {
            animation.play();
        }
        glyph.textContent = paused ? 'play_arrow' : 'pause';
        toggle.setAttribute('aria-label', paused ? 'Play animation' : 'Pause animation');
    };

    animation.addEventListener('DOMLoaded', () => {
        gallery.classList.add('live');
        setPaused(prefersReducedMotion());
    });

    animation.addEventListener('enterFrame', () => {
        meter.style.strokeDashoffset = String(CIRCUMFERENCE * (1 - animation.currentFrame / animation.totalFrames));
    });

    toggle.addEventListener('click', () => setPaused(!animation.isPaused));
}

// Walkthrough accordions — the highlights and contrast sections each hold a
// .walkthrough carrying a <ul class="points">; both auto-cycle independently on
// the same timebase pattern: the open point's rail fill is a CSS animation (one
// 6s draw, see Index.cshtml.scss), and its animationend folds that point and
// unfolds the next, so the bar and the fold can never drift. The points are
// PARALLEL, never a sequence — the cycle just walks them in DOM order, and the
// SCSS shows a rail only on the open one. Each advance also crossfades the md+
// illustration column to the open point's image (the i-th .shot img; the first
// ships .active from Razor for the no-JS page). The phone layout inlines each
// image inside its point's fold instead — pure CSS, nothing to drive here.
// Clicking a title jumps straight to it — dropping .active cancels the running
// fill, and a cancelled animation fires animationcancel, NOT animationend, so
// there's no double advance. Hovering an open point's copy is handled purely in
// the SCSS — it pauses the fill, which pauses the timer, so nothing here needs to
// know about it. Under reduced motion the SCSS strips the fill animation: no
// animationend ever fires and the cycle advances only by click.
document.querySelectorAll<HTMLElement>('.walkthrough').forEach((walk) => {
    const list = walk.querySelector<HTMLElement>('.points');
    const items = list ? Array.from(list.querySelectorAll<HTMLLIElement>('.point')) : [];
    const shots = Array.from(walk.querySelectorAll<HTMLImageElement>('.shot img'));

    if (!list || items.length < 2) return;

    let active = 0;

    const expand = (next: number): void => {
        items[active].classList.remove('active');
        shots[active]?.classList.remove('active');
        items[active].querySelector('.name button')?.setAttribute('aria-expanded', 'false');
        active = next;
        items[active].classList.add('active');
        shots[active]?.classList.add('active');
        items[active].querySelector('.name button')?.setAttribute('aria-expanded', 'true');
    };

    items.forEach((item, i) => {
        const title = item.querySelector<HTMLButtonElement>('.name button');
        title?.setAttribute('aria-expanded', i === active ? 'true' : 'false');
        title?.addEventListener('click', () => {
            if (i !== active) expand(i);
        });
        // One completed fill draw → fold this step, unfold the next (wrapping).
        item.querySelector<HTMLElement>('.fill')?.addEventListener('animationend', () => expand((i + 1) % items.length));
    });

    items[active].classList.add('active');
    list.classList.add('live');
});

// Community faces — the portrait fan assembles when scrolled into view and folds
// back behind the static centre face on the way out (TWO-WAY: .live toggles with
// intersection; the fold is the same CSS transition run in reverse — see
// Index.cshtml.scss). .fan is the armed state, added here so that without JS the
// fan just sits assembled. Skipped under prefers-reduced-motion: never armed, so
// it stays assembled and static.
const faces = document.querySelector<HTMLElement>('.scale .faces');

if (faces && !prefersReducedMotion()) {
    faces.classList.add('fan');

    const fanObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                faces.classList.toggle('live', entry.isIntersecting);
            });
        },
        { threshold: 0.5 },
    );

    fanObserver.observe(faces);
}

// Discover carousel — the shared conveyor engine plus a filter-pill group. The
// pills are this page's only addition: picking one shows only the cards whose
// data-cat carries that tag (the rest get MDB's .d-none) and rebuilds the
// conveyor (bullets + clones) over the visible set.
const track = document.querySelector<HTMLElement>('.discover .track');
const bulletBar = document.querySelector<HTMLElement>('.discover-nav .bullets');

if (track && bulletBar) {
    const carousel = createCarousel({ track, bulletBar, bulletLabel: (i) => `Go to scenario ${i + 1}` });

    // Filter pills — one active pick; "all" restores every card.
    const filters = Array.from(document.querySelectorAll<HTMLButtonElement>('.discover .filter'));
    filters.forEach((pill) => {
        pill.addEventListener('click', () => {
            if (pill.classList.contains('active')) {
                return;
            }
            filters.forEach((p) => {
                p.classList.toggle('active', p === pill);
                p.setAttribute('aria-pressed', String(p === pill));
            });
            const pick = pill.dataset.cat ?? 'all';
            carousel.allCards.forEach((card) => {
                const tags = (card.dataset.cat ?? '').split(' ');
                card.classList.toggle('d-none', pick !== 'all' && !tags.includes(pick));
            });
            carousel.setCards(carousel.allCards.filter((card) => !card.classList.contains('d-none')));
        });
    });
}

// ── Scroll-reveal ────────────────────────────────────────────────────────────
// The scale band's faces are deliberately NOT targets — they run the fan
// choreography above instead.
revealOnScroll(
    '.hero .copy > *, .hero .gallery, '
    + '.highlights .title, .highlights .lead, .highlights .shot, .highlights .points, '
    + '.contrast .title, .contrast .lead, .contrast .shot, .contrast .points, '
    + '.scale .title, .scale .lead, .scale .stat, '
    + '.discover .title, .discover .filters, .discover .track',
);
